// 計算結果表示コンポーネント

// React 18以降はJSXの自動変換によりReactインポートは不要
import { CalculationResult } from '../types';

interface ResultDisplayProps {
  result: CalculationResult;
}

function formatArea(area: number): string {
  if (area === 0) return '0';

  // Round to appropriate precision based on magnitude
  const absArea = Math.abs(area);
  let roundedArea: number;

  if (absArea < 1000) {
    roundedArea = Math.round(area);
  } else if (absArea < 10000) {
    roundedArea = Math.round(area / 10) * 10;
  } else {
    roundedArea = Math.round(area / 100) * 100;
  }

  return roundedArea.toLocaleString();
}

export function ResultDisplay({ result }: ResultDisplayProps) {
  const totalVeneerArea = result.veneerWidth * result.veneerHeight;
  const usedArea = result.totalPieces * result.optimalPieceWidth * result.optimalPieceHeight;
  const efficiency = (usedArea / totalVeneerArea) * 100;
  const wastePercentage = (result.leftoverArea / totalVeneerArea) * 100;

  return (
    <div className="card">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        計算結果
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 基本情報 */}
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-green-800 mb-3">
              最適配置
            </h3>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt className="text-green-700">部材名:</dt>
                <dd className="font-semibold text-green-900">{result.materialName}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-green-700">選択板材:</dt>
                <dd className="font-semibold text-green-900">
                  {result.selectedVeneer} ({result.veneerWidth} × {result.veneerHeight} mm)
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-green-700">取れる枚数:</dt>
                <dd className="font-bold text-2xl text-green-900">{result.totalPieces} 枚</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-green-700">配置方法:</dt>
                <dd className="font-semibold text-green-900">{result.orientation}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* 詳細情報 */}
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-800 mb-3">
              配置詳細
            </h3>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt className="text-blue-700">水平方向:</dt>
                <dd className="font-semibold text-blue-900">{result.horizontalCount} 枚</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-blue-700">垂直方向:</dt>
                <dd className="font-semibold text-blue-900">{result.verticalCount} 枚</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-blue-700">配置時のサイズ:</dt>
                <dd className="font-semibold text-blue-900">
                  {result.optimalPieceWidth} × {result.optimalPieceHeight} mm
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-blue-700">総余り面積:</dt>
                <dd className="font-semibold text-blue-900">
                  {formatArea(result.leftoverArea)} mm²
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* 効率性の表示 */}
      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          効率性分析
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {efficiency.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">材料効率</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {wastePercentage.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">廃材率</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {formatArea(totalVeneerArea)} mm²
            </div>
            <div className="text-sm text-gray-600">板材面積</div>
          </div>
        </div>
      </div>

      {/* 注意事項 */}
      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-amber-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-semibold text-amber-800">計算結果について</h4>
            <p className="mt-1 text-sm text-amber-700">
              この計算結果は刃幅を考慮した理論値です。
              実際の切断では、材料の反りや切断精度の影響で多少の誤差が生じる場合があります。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}